/**
 * This custom hook provides the functionality to retrieve users to be shown in the UI.
 */

import React, {useContext, useEffect, useState} from "react";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useSnackbar} from "notistack";
import {useConfirm} from "material-ui-confirm";
import ScoredScenesSectionOfGivenListSlim from "../../videosScored/overview/ScoredScenesSectionOfGivenListSlim";
import ScoredScenesSectionOfVideoSlim from "../../videosScored/overview/ScoredScenesSectionOfVideoSlim";
import {useIntl} from "react-intl";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";

const useListVideos = () => {

    // State storage of the users shown in the table
    const [videos, setVideos] = useState([])
    const [scoredScenesNumbers, setScoredScenesNumbers] = useState([])


    /* Current queue of video upload jobs - only required for re-fetching the videos in case this list changes.
       Otherwise, the user would need to "re-enter" the page to see the new video in the list in case he is currently
       on the video overview page. */
    const {backgroundJobs} = useContext(AppbarContentContext)

    // Loading indication
    const [loading, setLoading] = useState(true)
    const [deletionInProgress, setDeletionInProgress] = useState(false)
    const [deletionAttemptedVideo, setDeletionAttemptedVideo] = useState(null)

    const {signedInUser} = useContext(FirebaseAuthContext);

    const {enqueueSnackbar} = useSnackbar();
    const confirm = useConfirm();

    // Internationalization for simple strings
    const intl = useIntl();


    // Retrieving the already existing videos
    const fetchVideos = async () => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/videos?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const videosJson = await data.json();
                setVideos(videosJson)

                return videosJson
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.list.error',
                        defaultMessage: 'Something went wrong while listing the videos: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // Initial loading of the videos + automatic polling (for transcoding status changes)
    useEffect(() => {
        let interval

        fetchVideos()
            .then(async (videos) => {
                await updateScoredScenesNumberOfVideos()
                return videos
            })
            .then((videos) => {

                // Start the period checking for an updated transcoding status in case there is some transcoding currently in progress
                if (videos.find((v) => !v.vimeo.transcoded)) {

                    // Automatically checking the transcoding state (every 10 seconds)
                    interval = setInterval(async () => {
                        const videos = await fetchVideos()

                        // Stop the interval in case all videos are transcoded
                        if (!videos.find((v) => !v.vimeo.transcoded)) {
                            clearInterval(interval)
                        }
                    }, 10000);
                }
            })

        return () => clearInterval(interval);
    }, [])

    /* Re-fetch the list of videos in case the background jobs (currently only for video upload) change, which will make
       the video entry immediately visible for the user as soon as it is successfully uploaded and persisted. */
    useEffect(() => {
        fetchVideos()
    }, [backgroundJobs])

    const updateScoredScenesNumberOfVideos = async () => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/scoredScenes/functions/groupByVideo?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                const numbersList = await data.json();
                setScoredScenesNumbers(numbersList)
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.fetchNumberOfAssociatedScenes.error',
                        defaultMessage: 'Something went wrong while fetching the number of linked video scenes: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
    }

    const handleDeleteVideo = (video) => {
        setDeletionAttemptedVideo(video.key)
        return deletionAllowed(video)
            .then((checkResult) => {
                setDeletionAttemptedVideo(null)
                if (checkResult.allowed) {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.videos.dialog.delete.confirm.title',
                            defaultMessage: 'Delete Video.'
                        }),
                        content: scoredScenesNumbers[video.key] > 0
                            ? <div>
                                <div>
                                    {intl.formatMessage({
                                        id: 'app.videos.dialog.delete.confirm.linkedScenes.description',
                                        defaultMessage: 'Delete video and the following associated video scenes?'
                                    })}
                                </div>

                                <ScoredScenesSectionOfVideoSlim videoKey={video.key}/>
                            </div>
                            : <div>
                                {intl.formatMessage({
                                    id: 'app.videos.dialog.delete.confirm.noLinkedScenes.description',
                                    defaultMessage: 'Delete video?'
                                })}
                            </div>
                    })
                        .then(() => 'OK')
                        .catch(() => null)
                } else {
                    return confirm({
                        title: intl.formatMessage({
                            id: 'app.videos.dialog.delete.existingReferences.title',
                            defaultMessage: 'Deletion not allowed'
                        }),
                        content: <div>
                            <p>{intl.formatMessage({
                                id: 'app.videos.dialog.delete.existingReferences.description',
                                defaultMessage: 'The following video scenes are included in playlists'
                            })}:</p>
                            <ScoredScenesSectionOfGivenListSlim scoredScenes={checkResult.scoredScenes}/>
                        </div>
                    }).then(() => null).catch(() => null)
                }
            })
            .then((clickedButton) => {
                if (clickedButton === 'OK') {
                    return deleteVideo(video)
                }
                return null
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.delete.error',
                        defaultMessage: 'Something went wrong while deleting the video: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {
                setDeletionAttemptedVideo(null)
                setDeletionInProgress(false)
            })
    }

    const deletionAllowed = async (video) => {
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/videos/' + video.key + '/deletionAllowed?idToken=' + idToken)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                return data.json();
            })
    }

    const deleteVideo = async (video) => {
        setDeletionInProgress(true)
        const idToken = await signedInUser.getIdToken()
        return fetch('/reg-main/videos/' + video.key, {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken,
                videoData: video
            })
        }).then(async (data) => {
            // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
            if (data.status !== 200) {
                throw new Error((await data.json()).status)
            }

            await fetchVideos()
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.videos.messages.delete.success',
                    defaultMessage: 'Video deleted.'
                }), {
                    variant: 'success',
                    preventDuplicate: true,
                    autoHideDuration: 3000
                });
        })
    }

    return {
        videos,
        loading,
        handleDeleteVideo,
        deletionInProgress,
        scoredScenesNumbers,
        updateScoredScenesNumberOfVideos,
        deletionAttemptedVideo
    }
}
export default useListVideos;